import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { SensorData, SensorReading, SensorType } from '@/lib/sensor-types';
import { sensorManager } from '@/lib/sensor-integration';

interface LiveSensorDataStreamProps {
  sensorId: string;
  sensorType: SensorType;
  height?: number;
}

export const LiveSensorDataStream: React.FC<LiveSensorDataStreamProps> = ({
  sensorId,
  sensorType,
  height = 200
}) => {
  const [sensorData, setSensorData] = useState<SensorData | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);

  useEffect(() => {
    const updateData = () => {
      const data = sensorManager.getSensorData(sensorId);
      if (data) {
        setSensorData(data);
        setIsStreaming(Date.now() - data.lastUpdate < 5000);
      }
    };

    // Subscribe to sensor updates
    sensorManager.subscribe(sensorId, updateData);

    // Update every second
    const interval = setInterval(updateData, 1000);

    return () => clearInterval(interval);
  }, [sensorId]);

  const getChartData = () => {
    if (!sensorData || !sensorData.readings.length) return [];
    
    return sensorData.readings.slice(-50).map((reading, index) => ({
      time: index,
      value: reading.value,
      quality: reading.quality * 100
    }));
  };

  const getLatestReading = (): SensorReading | null => {
    if (!sensorData || !sensorData.readings.length) return null;
    return sensorData.readings[sensorData.readings.length - 1];
  };

  const getSensorUnit = () => {
    switch (sensorType) {
      case SensorType.HEART_RATE: return 'BPM';
      case SensorType.EEG: return 'μV';
      case SensorType.GSR: return 'μS';
      case SensorType.TEMPERATURE: return '°C';
      case SensorType.BREATHING: return 'RPM';
      default: return '';
    }
  };

  const getColorScheme = () => {
    switch (sensorType) {
      case SensorType.HEART_RATE: return { primary: '#ef4444', bg: 'bg-red-50' };
      case SensorType.EEG: return { primary: '#8b5cf6', bg: 'bg-purple-50' };
      case SensorType.GSR: return { primary: '#06b6d4', bg: 'bg-cyan-50' };
      case SensorType.TEMPERATURE: return { primary: '#f59e0b', bg: 'bg-amber-50' };
      case SensorType.BREATHING: return { primary: '#10b981', bg: 'bg-emerald-50' };
      default: return { primary: '#6b7280', bg: 'bg-gray-50' };
    }
  };

  const latestReading = getLatestReading();
  const chartData = getChartData();
  const colors = getColorScheme();

  return (
    <Card className={`${colors.bg} border-2`}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="capitalize">{sensorType.replace('_', ' ')}</span>
          <div className="flex items-center space-x-2">
            <Badge variant={isStreaming ? "default" : "secondary"}>
              {isStreaming ? "Live" : "Offline"}
            </Badge>
            {latestReading && (
              <Badge variant="outline" className="font-mono">
                {latestReading.value.toFixed(1)} {getSensorUnit()}
              </Badge>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="time" 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10 }}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10 }}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={colors.primary}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {latestReading && (
          <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-gray-500">Signal Quality</div>
              <div className="font-semibold">
                {(latestReading.quality * 100).toFixed(1)}%
              </div>
            </div>
            <div>
              <div className="text-gray-500">Last Update</div>
              <div className="font-semibold">
                {new Date(latestReading.timestamp).toLocaleTimeString()}
              </div>
            </div>
            <div>
              <div className="text-gray-500">Anomalies</div>
              <div className="font-semibold">
                {sensorData?.anomalies.length || 0}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};